import { CommonModule } from '@angular/common';
import { Component, Input, Output, EventEmitter, signal } from '@angular/core';

export interface TreeNode {
  id: string;
  label: string;
  icon?: string;
  children?: TreeNode[];
  data?: unknown;
}

/**
 * Recursive tree that supports expand/collapse per node and emits the selected node to the parent.
 */
@Component({
  selector: 'es-tree-view',
  imports: [CommonModule],
  template: `
    <ul class="text-sm" [class.pl-4]="depth > 0">
      @for (node of nodes; track node.id) {
        <li>
          <div
            class="flex items-center gap-2 px-2 py-1.5 rounded-lg cursor-pointer transition-colors"
            [class]="node.id === selectedId ? 'bg-echo-blue/10 text-echo-blue' : 'text-[var(--text-secondary)] hover:text-[var(--text)] hover:bg-[var(--bg-card-hover)]'"
            (click)="select(node)"
          >
            @if (node.children?.length) {
              <button
                class="w-4 h-4 flex items-center justify-center text-xs"
                (click)="toggle(node.id, $event)"
              >{{ isExpanded(node.id) ? '▼' : '▶' }}</button>
            } @else {
              <span class="w-4"></span>
            }
            @if (node.icon) {
              <span>{{ node.icon }}</span>
            }
            <span class="truncate">{{ node.label }}</span>
          </div>

          @if (node.children?.length && isExpanded(node.id)) {
            <es-tree-view
              [nodes]="node.children!"
              [selectedId]="selectedId"
              [depth]="depth + 1"
              (nodeSelected)="nodeSelected.emit($event)"
            ></es-tree-view>
          }
        </li>
      }
    </ul>
  `,
})
export class TreeViewComponent {
  @Input() nodes: TreeNode[] = [];
  @Input() selectedId: string | null = null;
  @Input() depth = 0;
  @Output() nodeSelected = new EventEmitter<TreeNode>();

  private expanded = signal<Set<string>>(new Set());

  isExpanded(id: string): boolean {
    return this.expanded().has(id);
  }

  toggle(id: string, event: Event) {
    event.stopPropagation();
    this.expanded.update(set => {
      const next = new Set(set);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  select(node: TreeNode) {
    this.nodeSelected.emit(node);
  }
}
